import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api.service';
import { PayrollBatchDetailPanel } from '../components/payroll/PayrollBatchDetailPanel';

type CollectionRow = {
  id: string;
  status: string;
  amountInformed: number | string;
  amountCollected: number | string;
  informedAt: string;
  publicEntity?: { code: string; name: string };
  batch?: { fileDate: string; fileName?: string | null };
};

function money(n: number | string) {
  return Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

export function PayrollBatchCollections() {
  const [rows, setRows] = useState<CollectionRow[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [banks, setBanks] = useState<any[]>([]);
  const [settleBankId, setSettleBankId] = useState('');
  const [settleAmount, setSettleAmount] = useState('');
  const [settleRef, setSettleRef] = useState('');

  const load = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await apiService.listPayrollBatchCollections(
        statusFilter ? { status: statusFilter } : undefined,
      );
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error al cargar');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [statusFilter]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setBusy(true);
    try {
      const [d, bankList] = await Promise.all([
        apiService.getPayrollBatchCollection(id),
        apiService.listBankAccounts(),
      ]);
      setDetail(d);
      setBanks(Array.isArray(bankList) ? bankList : []);
      const balance = Number(d.balance ?? Number(d.amountInformed) - Number(d.amountCollected));
      setSettleAmount(String(balance));
      setSettleBankId(bankList?.[0]?.id ?? '');
      setSettleRef('');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error al abrir detalle');
    } finally {
      setBusy(false);
    }
  };

  const balance = useMemo(() => {
    if (!detail) return 0;
    return Number(detail.balance ?? Number(detail.amountInformed) - Number(detail.amountCollected));
  }, [detail]);

  const settle = async () => {
    if (!selectedId || !settleBankId) return;
    setBusy(true);
    setError('');
    try {
      await apiService.settlePayrollBatchCollection(selectedId, {
        bankAccountId: settleBankId,
        amount: Number(settleAmount),
        reference: settleRef || undefined,
      });
      await openDetail(selectedId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Error al imputar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cobros pendientes entidades</h1>
          <p className="text-sm text-gray-600">
            Descuentos ya cerrados por TXT; liquidá el ingreso bancario (parcial o total).
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm"
        >
          <option value="">Todos</option>
          <option value="INFORMED">Informado</option>
          <option value="PARTIAL">Parcial</option>
          <option value="SETTLED">Liquidado</option>
        </select>
      </div>

      {error && <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2">Entidad</th>
              <th className="px-3 py-2">Archivo</th>
              <th className="px-3 py-2">Informado</th>
              <th className="px-3 py-2">Cobrado</th>
              <th className="px-3 py-2">Saldo</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const informed = Number(r.amountInformed);
              const collected = Number(r.amountCollected);
              return (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2">
                    {r.publicEntity?.code} — {r.publicEntity?.name}
                  </td>
                  <td className="px-3 py-2">
                    {r.batch?.fileDate
                      ? new Date(r.batch.fileDate).toLocaleDateString('es-AR')
                      : '—'}
                  </td>
                  <td className="px-3 py-2">{money(informed)}</td>
                  <td className="px-3 py-2">{money(collected)}</td>
                  <td className="px-3 py-2 font-medium">{money(informed - collected)}</td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => void openDetail(r.id)}
                      className="text-indigo-600 hover:underline"
                    >
                      Ver / imputar
                    </button>
                  </td>
                </tr>
              );
            })}
            {!busy && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                  No hay liquidaciones de lote
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && selectedId && (
        <div className="border rounded-lg bg-white p-4 space-y-4">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                {detail.publicEntity?.name} · {detail.status}
              </h2>
              <p className="text-sm text-gray-600">
                Informado {money(detail.amountInformed)} · Cobrado {money(detail.amountCollected)} ·
                Saldo {money(balance)}
              </p>
            </div>
            <button type="button" className="text-gray-500" onClick={() => { setDetail(null); setSelectedId(null); }}>
              Cerrar
            </button>
          </div>

          {detail.batch?.id && (
            <PayrollBatchDetailPanel batchId={detail.batch.id} compact />
          )}

          {detail.status !== 'SETTLED' && detail.status !== 'CANCELLED' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border-t pt-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Cuenta bancaria</label>
                <select
                  value={settleBankId}
                  onChange={(e) => setSettleBankId(e.target.value)}
                  className="w-full border rounded-md px-2 py-2 text-sm"
                >
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.bankName} {b.accountNumber || ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Monto</label>
                <input
                  type="number"
                  step="0.01"
                  value={settleAmount}
                  onChange={(e) => setSettleAmount(e.target.value)}
                  className="w-full border rounded-md px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Referencia</label>
                <input
                  value={settleRef}
                  onChange={(e) => setSettleRef(e.target.value)}
                  className="w-full border rounded-md px-2 py-2 text-sm"
                  placeholder="Opcional"
                />
              </div>
              <button
                type="button"
                disabled={busy || !settleBankId}
                onClick={() => void settle()}
                className="bg-indigo-600 text-white rounded-md px-3 py-2 text-sm disabled:opacity-50"
              >
                Imputar depósito
              </button>
            </div>
          )}

          <div>
            <h3 className="font-medium mb-2">Depósitos imputados</h3>
            <ul className="text-sm space-y-1">
              {(detail.settlements || []).map((s: any) => (
                <li key={s.id} className="border-t py-1">
                  {new Date(s.settledAt).toLocaleString('es-AR')} · {money(s.amount)} ·{' '}
                  {s.bankAccount?.bankName || s.bankAccountId}
                  {s.reference ? ` · ${s.reference}` : ''}
                </li>
              ))}
              {(detail.settlements || []).length === 0 && (
                <li className="text-gray-500">Todavía no hay depósitos</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
